import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoading } from "@/components/common/Loading";
import { RagBadge } from "@/components/common/RagBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtDate, ORG_STATUS } from "@/lib/format";
import { CompanyDossier } from "@/components/CompanyDossier";
import { ModuleAccessEditor } from "@/components/ModuleAccessEditor";
import { toast } from "sonner";

export default function Settings() {
  const { orgId } = useOrg();
  const { user, isSuper } = useAuth();
  const [org, setOrg] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const canEditOrg = ["amden_super_admin", "amden_consultant", "client_admin"].includes(user?.role);

  useEffect(() => {
    if (!orgId) return;
    api.get(`/organisations/${orgId}`).then((r) => { setOrg(r.data); setForm(r.data); });
  }, [orgId]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        company_name: form.company_name, trading_name: form.trading_name, industry: form.industry,
        employees: form.employees, primary_contact: form.primary_contact, email: form.email,
        telephone: form.telephone, address: form.address, website: form.website, notes: form.notes,
      };
      const { data } = await api.patch(`/organisations/${orgId}`, payload);
      setOrg(data);
      toast.success("Organisation updated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!orgId) return <PageLoading />;

  return (
    <div data-testid="settings-page">
      <PageHeader eyebrow="Configuration" title="Settings" subtitle="Manage your organisation profile and account." icon="Settings" />

      <Tabs defaultValue="org">
        <TabsList className="mb-5 bg-[#E4E8EF]">
          <TabsTrigger value="org" data-testid="tab-org">Organisation</TabsTrigger>
          <TabsTrigger value="dossier" data-testid="tab-dossier">Company Dossier</TabsTrigger>
          {isSuper && <TabsTrigger value="access" data-testid="tab-access">Access</TabsTrigger>}
          <TabsTrigger value="account" data-testid="tab-account">My account</TabsTrigger>
        </TabsList>

        <TabsContent value="org">
          {!org ? <PageLoading /> : (
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
              <div className="amden-card p-6 lg:col-span-2">
                <h3 className="mb-4 font-display text-base font-bold">Business details</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Company name" value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} readOnly={!canEditOrg} testId="org-company-name" />
                  <Field label="Trading name" value={form.trading_name} onChange={(v) => setForm({ ...form, trading_name: v })} readOnly={!canEditOrg} />
                  <Field label="Industry" value={form.industry} onChange={(v) => setForm({ ...form, industry: v })} readOnly={!canEditOrg} />
                  <Field label="Employees" value={form.employees} onChange={(v) => setForm({ ...form, employees: v })} readOnly={!canEditOrg} />
                  <Field label="Primary contact" value={form.primary_contact} onChange={(v) => setForm({ ...form, primary_contact: v })} readOnly={!canEditOrg} />
                  <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} readOnly={!canEditOrg} />
                  <Field label="Telephone" value={form.telephone} onChange={(v) => setForm({ ...form, telephone: v })} readOnly={!canEditOrg} />
                  <Field label="Website" value={form.website} onChange={(v) => setForm({ ...form, website: v })} readOnly={!canEditOrg} />
                </div>
                <div className="mt-4"><Label>Address</Label><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} disabled={!canEditOrg} className="mt-1.5" /></div>
                <div className="mt-4"><Label>Notes</Label><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} disabled={!canEditOrg} className="mt-1.5" /></div>
                {canEditOrg && <Button onClick={save} disabled={saving} className="mt-5 bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="save-org-btn">{saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Save changes"}</Button>}
              </div>

              <div className="space-y-5">
                <div className="amden-card p-6">
                  <h3 className="mb-3 font-display text-base font-bold">Consultancy</h3>
                  <div className="space-y-3 text-sm">
                    <Row label="Status" value={<RagBadge rag={ORG_STATUS[org.status]?.rag}>{ORG_STATUS[org.status]?.label}</RagBadge>} />
                    <Row label="Package" value={org.package} />
                    <Row label="Consultant" value={org.consultant_name} />
                    <Row label="Start date" value={fmtDate(org.start_date)} />
                    <Row label="Review date" value={fmtDate(org.review_date)} />
                    <Row label="Year end" value={org.financial_year_end} />
                  </div>
                </div>
                {org.business_units?.length > 0 && (
                  <div className="amden-card p-6">
                    <h3 className="mb-3 font-display text-base font-bold">Business units</h3>
                    <div className="flex flex-wrap gap-2">{org.business_units.map((u) => <span key={u} className="rounded-full bg-[#F4F6F9] px-3 py-1 text-xs font-medium text-[#1E2D44]/70">{u}</span>)}</div>
                  </div>
                )}
                <OnboardingCard orgId={orgId} />
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="dossier">
          <CompanyDossier orgId={orgId} />
        </TabsContent>

        {isSuper && (
          <TabsContent value="access">
            <div className="amden-card max-w-2xl p-6">
              <h3 className="mb-1 font-display text-base font-bold">Client module access</h3>
              <p className="mb-4 text-sm text-[#1E2D44]/55">Turn areas of the workspace on or off for {org?.company_name || "this client"}'s own users.</p>
              <ModuleAccessEditor orgId={orgId} orgName={org?.company_name} />
            </div>
          </TabsContent>
        )}

        <TabsContent value="account">
          <div className="amden-card max-w-lg p-6">
            <div className="mb-4 flex items-center gap-3">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#FFB000] text-xl font-bold text-[#0B1729]">{user.name[0]}</span>
              <div><div className="font-display text-lg font-bold">{user.name}</div><div className="text-sm text-[#1E2D44]/55">{user.email}</div></div>
            </div>
            <div className="space-y-3 border-t border-[#EEF1F6] pt-4 text-sm">
              <Row label="Role" value={user.role_label} />
              <Row label="Title" value={user.title} />
              <Row label="Financial access" value={user.financial_access || ["amden_super_admin", "amden_consultant"].includes(user.role) ? <RagBadge rag="green">Granted</RagBadge> : <RagBadge rag="grey">Restricted</RagBadge>} />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function OnboardingCard({ orgId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { if (orgId) api.get(`/organisations/${orgId}/onboarding`).then((r) => setData(r.data)).catch(() => {}); }, [orgId]);
  return (
    <div className="amden-card p-6" data-testid="onboarding-checklist">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-display text-base font-bold">Onboarding checklist</h3>
        {data && <span className="text-sm font-semibold text-[#0B1729]">{data.completed}/{data.total} · {data.progress}%</span>}
      </div>
      <div className="mb-4 h-2 w-full overflow-hidden rounded-full bg-[#E4E8EF]"><div className="h-full rounded-full bg-[#FFB000] transition-all" style={{ width: `${data?.progress || 0}%` }} /></div>
      <div className="space-y-1">
        {(data?.items || []).map((it) => (
          <button key={it.key} onClick={() => navigate(it.link)} className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-[#F4F6F9]" data-testid={`onboarding-item-${it.key}`}>
            {it.done ? <Icons.CheckCircle2 size={17} className="shrink-0 text-[#3D8B65]" /> : <Icons.Circle size={17} className="shrink-0 text-[#1E2D44]/25" />}
            <div className="min-w-0 flex-1">
              <div className={`text-sm ${it.done ? "text-[#1E2D44]/75" : "font-medium text-[#0B1729]"}`}>{it.label}</div>
              <div className="text-[11px] text-[#1E2D44]/50">{it.detail}</div>
            </div>
            <Icons.ChevronRight size={14} className="shrink-0 text-[#1E2D44]/25" />
          </button>
        ))}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, readOnly, testId }) {  return <div><Label>{label}</Label><Input value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={readOnly} className="mt-1.5" data-testid={testId} /></div>;
}
function Row({ label, value }) {
  return <div className="flex items-center justify-between"><span className="text-[#1E2D44]/55">{label}</span><span className="font-medium text-[#0B1729]">{value || "—"}</span></div>;
}
